import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Paper, Skeleton, Stack } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useFieldArray, useForm, Control, FieldValues } from "react-hook-form";
import { toast } from "react-toastify";
import agent from "../../../app/api/agent";
import AutocompleteInputOption from "../../../app/models/autocompleteInputOption";
import { Order } from "../../../app/models/order";
import { useAppDispatch, useAppSelector } from "../../../app/store/configureStore";
import { fetchClientOrdersAsync, setClientOrder } from "./clientOrdersSlice";
import { clientOrderValidationSchema } from "./clientOrderValidation";
import AppReturn from "../../../app/components/AppReturn";
import AppBreadcrumbs from "../../../app/components/AppBreadcrumbs";
import dayjs from "dayjs";
import { orderFormCalculateSum, states } from "../../../app/util/util";
import { OrderFormValues } from "../../../app/models/orderFormValues";
import OrderFormBasicInformation from "../../../app/components/Order/OrderFormBasicInformation";
import OrderFormPayerData from "../../../app/components/Order/OrderFormPayerData";
import OrderFormBenefitients from "../../../app/components/Order/OrderFormBenefitients";
import OrderValueBox from "../../../app/components/Order/OrderValueBox";
import OrderFormEducationEffectsModal from "../../../app/components/Order/OrderFormEducationEffectsModal";
import DraftRestoreButton from "../../../app/components/Draft/DraftRestoreButton";
import { useServicePriceFetching } from "../../../app/hooks/useServicePriceFetching";
import { useEducationEffects } from "../../../app/hooks/useEducationEffects";
import { useEnhancedFormDraft } from "../../../app/hooks/useEnhancedFormDraft";
import AppUnsavedChangesDialog from "../../../app/components/AppUnsavedChangesDialog";
import { useBlockNavigation } from "../../../app/hooks/useBlockNavigation";
import DraftRestoreDialog from "../../../app/components/Draft/DraftRestoreDialog";

interface Props {
    order?: Order;
    copyFromOrder?: Order;
    cancelEdit: () => void;
}

export default function ClientOrderForm({ order, copyFromOrder, cancelEdit }: Props) {
    const { control, reset, handleSubmit, setValue, watch, getValues,
        formState: { errors, isDirty, isSubmitting } } = useForm<OrderFormValues>({
            resolver: yupResolver(clientOrderValidationSchema),
            defaultValues: {
                serviceId: 0,
                customeClientServiceName: '',
                preposedDateTime: '',
                placeOfServiceAndAnotherInformations: '',
                payerInstitutionName: '',
                payerLastName: '',
                payerFirstName: '',
                payerStreet: '',
                payerBuilding: '',
                payerZip: '',
                payerCity: '',
                payerState: '',
                payerCountry: '',
                payerNIP: '',
                rows: [],
                totalItem: 1
            }
        });

    const dispatch = useAppDispatch();
    const [possibleServices, setPossibleServices] = useState<AutocompleteInputOption[]>([]);
    const serviceId = watch('serviceId');
    const user = useAppSelector(state => state.account.user);
    const isAdmin = !!(user && user.roles?.includes('Admin'));

    const [manualServiceChange, setManualServiceChange] = useState(false);
    const [unitDiscountedPrice, setUnitDiscountedPrice] = useState<number>(0);
    const [sum, setSum] = useState<number>(0);
    const [totalItemRows, setTotalItemRows] = useState(0);
    const [isLoading, setIsLoading] = useState<boolean>(!!order || !!copyFromOrder); // Show loading if editing or copying

    // State for unsaved changes dialog
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
    // State to store proceed and cancel functions from the blocker
    const [proceedNavigationFn, setProceedNavigationFn] = useState<() => void>(() => { });
    const [cancelNavigationFn, setCancelNavigationFn] = useState<() => void>(() => { });
    // State for manual navigation (cancel button)
    const [pendingManualNavigation, setPendingManualNavigation] = useState<(() => void) | null>(null);

    // Draft restore dialog state
    const [showDraftRestoreDialog, setShowDraftRestoreDialog] = useState(false);

    const handleSetTotalItemRows = (count: number) => {
        setTotalItemRows(count);
        setSum(orderFormCalculateSum(count, unitDiscountedPrice));
    }

    const formType = 'clientOrder';
    const formId = 'new'; // Always use 'new' for new forms

    const fieldsToExcludeFromDraft = [
        'paymentOrderState'
    ];

    // Use our enhanced form draft hook
    const {
        storedDraft,
        isRestoringDraft,
        draftRestorationRef,
        shouldFetchPrice,
        restoredServiceId,
        setShouldFetchPrice,
        setRestoredServiceId,
        handleRestoreDraft,
        clearDraft,
        getDraftSummary,
        draftShownOnLoad,
        draftDeleteCountdown,
        resetCountdown,
        getDraft,
        setDraftDeleteCountdown
    } = useEnhancedFormDraft<OrderFormValues>(
        // Only use draft when creating a new form, not editing
        order ? '' : formType,
        order ? '' : formId,
        { reset, setValue, getValues, isDirty },
        {
            excludeFields: fieldsToExcludeFromDraft,
        }
    );

    // Get draft summary for dialog
    const draftSummary = storedDraft ? getDraftSummary(storedDraft) : { draftDate: '', fields: [] };

    // Check for drafts on component mount
    useEffect(() => {
        // Just make sure we've checked for drafts
        if (!storedDraft && getDraft) {
            const existingDraft = getDraft();
            if (existingDraft) {
                console.log('Found draft on component mount');
                // The useEnhancedFormDraft hook will handle state updating
            }
        }
    }, [getDraft, storedDraft]);

    // Show draft restore dialog when a draft is found on load
    useEffect(() => {
        if (!order && storedDraft && draftShownOnLoad) {
            setShowDraftRestoreDialog(true);
        }
    }, [storedDraft, draftShownOnLoad, order]);

    // Use route blocker to prevent leaving with unsaved changes
    useBlockNavigation(
        isDirty,
        (proceed, cancel) => {
            // Store the navigation functions
            setProceedNavigationFn(() => proceed);
            setCancelNavigationFn(() => cancel);
            // Show the confirmation dialog
            setShowUnsavedChangesDialog(true);
        }
    );

    // Use our education effects hook
    const {
        educationEffects,
        setEducationEffects,
        openModal,
        handleOpenModal,
        handleCloseModal,
        addNewEffect,
        updateEffect,
        removeEffect,
        handleSaveEducationEffects
    } = useEducationEffects({
        serviceId,
        manualServiceChange,
        setValue,
        watch,
        isRestoringDraft,
        draftRestorationRef
    });

    // Use service price fetching hook
    useServicePriceFetching({
        serviceId,
        isRestoringDraft,
        shouldFetchPrice,
        restoredServiceId,
        order,
        setShouldFetchPrice,
        setRestoredServiceId,
        setUnitDiscountedPrice,
        draftRestorationRef
    });

    // Set up field array for beneficiaries
    const { fields, append, remove } = useFieldArray({
        control,
        name: "rows"
    });

    // Function to handle cancel with unsaved changes check
    // Replace the existing handleCancel function with this:
    const handleCancel = useCallback(() => {
        if (isDirty) {
            // For manual navigation (buttons), we use a different approach
            setShowUnsavedChangesDialog(true);
            setPendingManualNavigation(() => {
                return () => {
                    // Don't clear drafts - this preserves the draft when navigating away
                    cancelEdit();
                };
            });
        } else {
            // If form is not dirty, we can navigate away directly
            // Also don't clear drafts here
            cancelEdit();
        }
    }, [isDirty, cancelEdit]);

    const handleConfirmNavigation = useCallback(() => {
        setShowUnsavedChangesDialog(false);

        // Handle navigation based on source:
        // 1. If it's a router-based navigation
        if (proceedNavigationFn) {
            proceedNavigationFn();
        }

        // 2. If it's a manual navigation (from cancel button)
        if (pendingManualNavigation) {
            pendingManualNavigation();
            setPendingManualNavigation(null);
        }
    }, [proceedNavigationFn, pendingManualNavigation]);

    // Function to cancel navigation away (stay on page)
    const handleCancelNavigation = useCallback(() => {
        setShowUnsavedChangesDialog(false);

        // If it's a router-based navigation
        if (cancelNavigationFn) {
            cancelNavigationFn();
        }

        // For manual navigation, just close the dialog
        setPendingManualNavigation(null);
    }, [cancelNavigationFn]);

    // Prevent accidental window close or navigation away
    useEffect(() => {
        // Only add the event listener if the form has unsaved changes
        if (isDirty) {
            const handleBeforeUnload = (e: BeforeUnloadEvent) => {
                e.preventDefault();
                e.returnValue = ''; // Chrome requires returnValue to be set
                return '';
            };

            window.addEventListener('beforeunload', handleBeforeUnload);
            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
            };
        }
    }, [isDirty]);

    // Load possible services
    useEffect(() => {
        agent.Services.fetchPossibleServices()
            .then(result => setPossibleServices(result))
            .catch(error => console.log(error));
    }, []);

    // Load existing order data if editing
    useEffect(() => {
        if (order && !isDirty) {
            const rows = (order?.benefitients ?? '')
                .split(';')
                .filter(item => item.trim() !== '') // Filter out empty strings
                .map(item => {
                    const [firstName, lastName, pesel, birthPlace, birthDate] = item.split('~');
                    return { firstName, lastName, pesel, birthPlace, birthDate };
                });
            reset({ ...order, rows, preposedDateTime: dayjs(order.preposedDateTime).toISOString() });
            setValue('educationEffects', order.educationEffects, { shouldValidate: true });
            setEducationEffects(order.educationEffects?.split(';') || []);
            setIsLoading(false); // Order data loaded
        }
    }, [order, reset, isDirty, setValue, setEducationEffects]);

    // Load data from copied order (copyFromOrder)
    useEffect(() => {
        if (copyFromOrder && !order) {
            const rows = (copyFromOrder.benefitients ?? '')
                .split(';')
                .filter(item => item.trim() !== '') // Filter out empty strings
                .map(item => {
                    const [firstName, lastName, pesel, birthPlace, birthDate] = item.split('~');
                    // Resetuj certId dla każdego uczestnika (nowe zamówienie = nowe certyfikaty)
                    return { firstName, lastName, pesel, birthPlace, birthDate, certId: '' };
                });
            
            reset({
                serviceId: copyFromOrder.serviceId,
                customeClientServiceName: copyFromOrder.customeClientServiceName || '',
                preposedDateTime: '', // Użytkownik musi podać nową datę
                placeOfServiceAndAnotherInformations: copyFromOrder.placeOfServiceAndAnotherInformations || '',
                payerInstitutionName: copyFromOrder.payerInstitutionName || '',
                payerLastName: copyFromOrder.payerLastName || '',
                payerFirstName: copyFromOrder.payerFirstName || '',
                payerStreet: copyFromOrder.payerStreet || '',
                payerBuilding: copyFromOrder.payerBuilding || '',
                payerZip: copyFromOrder.payerZip || '',
                payerCity: copyFromOrder.payerCity || '',
                payerState: copyFromOrder.payerState || '',
                payerCountry: copyFromOrder.payerCountry || '',
                payerNIP: copyFromOrder.payerNIP || '',
                rows,
                totalItem: rows.length || 1,
                educationEffects: copyFromOrder.educationEffects || '',
                trainingProgram: copyFromOrder.trainingProgram || '',
            });
            
            if (copyFromOrder.educationEffects) {
                setValue('educationEffects', copyFromOrder.educationEffects, { shouldValidate: true });
                setEducationEffects(copyFromOrder.educationEffects.split(';') || []);
            }
            
            // Pobierz cenę usługi bezpośrednio (nie używamy setManualServiceChange, bo to nadpisałoby efekty kształcenia)
            if (copyFromOrder.serviceId) {
                agent.Services.price(copyFromOrder.serviceId)
                    .then(response => setUnitDiscountedPrice(response))
                    .catch(error => console.error('Błąd podczas pobierania ceny usługi:', error));
            }
            setIsLoading(false); // Copy from order data loaded
        }
    }, [copyFromOrder, order, reset, setValue, setEducationEffects]);

    // Show validation errors
    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            const errorMessages: string[] = [];
            const seenErrors = new Set<string>(); // Aby uniknąć zdublowanych komunikatów
            
            // Funkcja pomocnicza do rekurencyjnego zbierania błędów
            const extractErrors = (obj: any, path: string = '') => {
                if (!obj) return;
                
                if (Array.isArray(obj)) {
                    // Jeśli to tablica (np. rows)
                    obj.forEach((item, index) => {
                        if (item && typeof item === 'object') {
                            Object.entries(item).forEach(([, value]: [string, any]) => {
                                if (value?.message) {
                                    let errorMsg = '';
                                    if (path.includes('rows')) {
                                        // Formatuj błędy z beneficjentów - tylko wiadomość bez nazwy pola
                                        errorMsg = `Uczestnik ${index + 1} - ${value.message}`;
                                    } else {
                                        errorMsg = value.message;
                                    }
                                    
                                    // Dodaj tylko jeśli jeszcze nie ma tego błędu
                                    if (!seenErrors.has(errorMsg)) {
                                        seenErrors.add(errorMsg);
                                        errorMessages.push(errorMsg);
                                    }
                                }
                            });
                        }
                    });
                } else if (typeof obj === 'object') {
                    // Jeśli to obiekt
                    Object.entries(obj).forEach(([key, value]: [string, any]) => {
                        if (value?.message) {
                            if (!seenErrors.has(value.message)) {
                                seenErrors.add(value.message);
                                errorMessages.push(value.message);
                            }
                        } else if (key === 'rows' && Array.isArray(value)) {
                            extractErrors(value, 'rows');
                        }
                    });
                }
            };
            
            extractErrors(errors);
            
            // Wyświetl maksymalnie 3 błędy, aby nie zalegały komunikaty
            const messagesToShow = errorMessages.slice(0, 3);
            if (messagesToShow.length > 0) {
                const suffix = errorMessages.length > 3 ? '...' : '';
                toast.error(`Sprawdź formularz: ${messagesToShow.join(', ')}${suffix}`);
            }
        }
    }, [errors]);

    // Handle form submission
    async function handleSubmitData(data: OrderFormValues) {
        if (!data.serviceId) {
            toast.error('Wybierz egzamin przed zapisaniem formularza');
            return;
        }

        try {
            // Calculate benefitients string and totalItem
            data.benefitients = data.rows.map(row =>
                `${row.firstName}~${row.lastName}~${row.pesel}~${row.birthPlace}~${row.birthDate}`
            ).join(';');

            // Always set totalItem based on actual rows count
            data.totalItem = data.rows.length;

            let response: Order;
            if (order) {
                // Updating existing order
                response = await agent.Client.Orders.update(data);
                toast.success('Uaktualniono zamówienie');
                // No need to clear drafts when editing
            } else {
                // Creating new order
                response = await agent.Client.Orders.create(data);
                toast.success('Utworzono zamówienie');

                // Only clear drafts when creating new orders
                clearDraft();
                setDraftDeleteCountdown(0);
            }

            // Save the order to the store, clear the draft, and navigate away
            dispatch(setClientOrder(response));

            // Make sure we really clear the draft completely
            clearDraft();
            setDraftDeleteCountdown(0);

            cancelEdit();

            // Refresh the orders list
            dispatch(fetchClientOrdersAsync());
        } catch (error) {
            console.log(error);
            toast.error('Wystąpił błąd podczas zapisywania zamówienia');
        }
    }

    // Fill with test data (admin only)
    const fillWithTestData = () => {
        const testData: OrderFormValues = {
            serviceId: 1,
            serviceName: 'Testowy serviceName',
            customeClientServiceName: 'Testowy Egzamin',
            preposedDateTime: dayjs().toISOString(),
            placeOfServiceAndAnotherInformations: 'Testowe miejsce',
            clientComment: 'Testowy komentarz',
            trainingProgram: 'Testowy program szkoleniowy',
            educationEffects: 'Efekt1;Efekt2;Efekt3',
            payerClientName: 'Testowa Instytucja',
            payerClientAddress: 'Adres',
            payerClientNIP: 'NIP',
            payerInstitutionName: 'Testowa Instytucja',
            payerLastName: 'Kowalski',
            payerFirstName: 'Jan',
            payerStreet: 'Testowa 1',
            payerBuilding: '1',
            payerPremises: '2',
            payerZip: '00-001',
            payerCity: 'Warszawa',
            payerState: states[0],
            payerCountry: 'Polska',
            payerNIP: '1234567890',
            rows: [
                {
                    firstName: 'Jan',
                    lastName: 'Kowalski',
                    pesel: '12345678901',
                    birthPlace: 'Warszawa',
                    birthDate: '01.01.1990'
                },
                {
                    firstName: 'Anna',
                    lastName: 'Nowak',
                    pesel: '09876543210',
                    birthPlace: 'Kraków',
                    birthDate: '02.02.1985'
                }
            ],
            totalItem: 2
        };
        reset(testData);
        setEducationEffects(testData.educationEffects?.split(';') || []);
    };

    // Handlers for draft restore dialog
    const handleRestoreDialogClose = () => {
        setShowDraftRestoreDialog(false);
        // Reset the countdown timer when the dialog is closed without restoring
        if (storedDraft) {
            resetCountdown(); // Start 60-second countdown to delete the draft
        }
    };

    const handleRestoreDraftFromDialog = () => {
        if (storedDraft) {
            handleRestoreDraft(storedDraft);
            setShowDraftRestoreDialog(false);
        }
    };

    const handleDeleteDraft = () => {
        clearDraft();
        setShowDraftRestoreDialog(false);
    };

    const handleDraftRestoreButtonClick = () => {
        setShowDraftRestoreDialog(true);
        // Reset countdown when user clicks the button to re-open dialog
        resetCountdown();
    };

    return (
        <>
            <AppBreadcrumbs />
            <AppReturn title={"Tworzenie/edycja zamówienia"} cancelEdit={handleCancel} />
            <Box component={Paper} sx={{ p: 4 }}>
                {/* Show the restore button floating on the side if there's a draft and we're not editing */}
                {!order && storedDraft && (
                    <DraftRestoreButton
                        formType={formType}
                        formId={formId}
                        draft={storedDraft}
                        onRestore={handleDraftRestoreButtonClick}
                        countdown={draftDeleteCountdown}
                    />
                )}

                <form onSubmit={handleSubmit(handleSubmitData)}>
                    {isLoading ? (
                        <Box sx={{ p: 4 }}>
                            <Stack spacing={3}>
                                <Skeleton variant="text" height={40} />
                                <Stack spacing={2}>
                                    <Skeleton variant="rounded" height={60} />
                                    <Skeleton variant="rounded" height={60} />
                                    <Skeleton variant="rounded" height={60} />
                                </Stack>
                                <Skeleton variant="rounded" height={200} />
                                <Stack spacing={1}>
                                    <Skeleton variant="text" height={30} width="60%" />
                                    <Skeleton variant="rounded" height={60} />
                                    <Skeleton variant="rounded" height={60} />
                                    <Skeleton variant="rounded" height={60} />
                                </Stack>
                                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                                    <Skeleton variant="rounded" height={40} width={100} />
                                    <Skeleton variant="rounded" height={40} width={100} />
                                </Stack>
                            </Stack>
                        </Box>
                    ) : (
                        <>
                    {isAdmin && (
                        <Box display='flex' justifyContent='center'>
                            <Button onClick={fillWithTestData} variant='outlined' color='secondary'>Wypełnij Danymi</Button>
                        </Box>
                    )}

                    <OrderFormBasicInformation
                        order={order}
                        control={control}
                        possibleServices={possibleServices}
                        watch={watch}
                        setValue={setValue}
                        handleOpenModal={handleOpenModal}
                        setManualServiceChange={() => setManualServiceChange(true)}
                        isAdmin={false}
                        setEducationEffects={setEducationEffects}
                    />
                    <OrderFormPayerData control={control} setValue={setValue} />
                    <OrderFormBenefitients
                        control={control as unknown as Control<FieldValues>}
                        fields={fields}
                        append={append as unknown as (value: FieldValues | FieldValues[]) => void}
                        remove={remove}
                        handleSetTotalItemRows={handleSetTotalItemRows}
                        setValue={setValue}
                        watch={watch}
                    />
                    <OrderValueBox sum={sum} unitDiscountedPrice={unitDiscountedPrice} totalItem={totalItemRows} />

                    <Box display='flex' justifyContent='space-between' sx={{ mt: 3 }}>
                        <Box>
                            <Button
                                onClick={handleCancel}
                                variant='outlined'
                                color='primary'
                                sx={{ mr: 2 }}
                            >
                                Anuluj
                            </Button>
                        </Box>
                        <LoadingButton
                            loading={isSubmitting}
                            disabled={!isDirty}
                            type='submit'
                            variant='outlined'
                            color='success'
                        >
                            Zapisz
                        </LoadingButton>
                    </Box>

                    <OrderFormEducationEffectsModal
                        openModal={openModal}
                        handleCloseModal={handleCloseModal}
                        educationEffects={educationEffects}
                        updateEffect={updateEffect}
                        removeEffect={removeEffect}
                        addNewEffect={addNewEffect}
                        handleSaveEducationEffects={handleSaveEducationEffects}
                    />
                        </>
                    )}
                </form>
            </Box>

            {/* Regular unsaved changes dialog */}
            <AppUnsavedChangesDialog
                open={showUnsavedChangesDialog}
                onClose={handleCancelNavigation}
                onConfirm={handleConfirmNavigation}
            />

            {!order && storedDraft && draftDeleteCountdown > 0 && (
                <DraftRestoreButton
                    formType={formType}
                    formId={formId}
                    draft={storedDraft}
                    onRestore={handleDraftRestoreButtonClick}
                    countdown={draftDeleteCountdown}
                />
            )}

            <DraftRestoreDialog
                open={showDraftRestoreDialog}
                onClose={handleRestoreDialogClose}
                onRestore={handleRestoreDraftFromDialog}
                onDelete={handleDeleteDraft}
                draftSummary={draftSummary}
            />
        </>
    );
}